// 🎨 헤더 액션 바 컴포넌트
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { formatDate } from '../../orderDetail/utils';
import type { Application } from '../../../../types/application';

interface HeaderActionsProps {
  application: Application;
  loading: boolean;
  isDeleting: boolean;
  onBack: () => void;
  onRefresh: () => void;
  onDelete?: () => Promise<void>;
  onDeleteClick: () => void;
}

/**
 * 애플리케이션 헤더 액션 바 컴포넌트
 * - 뒤로가기 버튼
 * - 제목 및 접수일 표시
 * - 새로고침 버튼
 * - 삭제 버튼 (선택적)
 */
export const HeaderActions: React.FC<HeaderActionsProps> = ({
  application,
  loading,
  isDeleting,
  onBack,
  onRefresh,
  onDelete,
  onDeleteClick,
}) => {
  return (
    <Stack 
      direction="row" 
      alignItems="center" 
      justifyContent="space-between"
      sx={{ mb: 2 }}
    >
      {/* 왼쪽: 뒤로가기 + 제목 */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Tooltip title="신청 목록으로 돌아가기">
          <IconButton
            onClick={onBack}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 44,
              height: 44,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5,
            }}
          >
            신청서 #{application.application_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(application.created_at)} 접수
          </Typography>
        </Box>
      </Stack>

      {/* 오른쪽: 액션 버튼들 */}
      <Stack direction="row" spacing={1}>
        <Tooltip title="새로고침">
          <IconButton
            onClick={onRefresh}
            disabled={loading}
            sx={{
              bgcolor: 'grey.100',
              '&:hover': { bgcolor: 'grey.200' },
              transition: 'all 0.2s ease',
            }}
          >
            <RefreshIcon sx={{ 
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(360deg)' },
              },
            }} />
          </IconButton>
        </Tooltip>

        {/* 🗑️ 삭제 버튼 */}
        {onDelete && (
          <Tooltip title="신청서 삭제">
            <IconButton
              onClick={onDeleteClick}
              disabled={loading || isDeleting}
              sx={{
                bgcolor: 'error.light',
                color: 'white',
                '&:hover': { 
                  bgcolor: 'error.main',
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};